import { defineLocalStore } from '@/utils/localStore'
import { ref } from 'vue'
import { handleAsync } from 'best-modules/plugins'
import { DocumentAgreementContext } from './types'
import { useDocumentAgreementEntity } from './composables/documentAgreementEntity'
import { useFiles } from './composables/files'
import { useParticipants } from './composables/participants'
import { useRelatedDocuments } from './composables/relatedDocuments'
import { useRelatedSignatures } from './composables/relatedSignatures'
import { useLogs } from './composables/logs'
import { useRoutes } from './composables/routes'
import { useExtraDocuments } from './composables/extraDocuments'

export const {
  useLocalStore: useDocumentAgreement,
  initLocalStore: initDocumentAgreement,
} = defineLocalStore('document-agreement', () => {
  const { subscribeToSocket, setCredentials, ...entityState } =
    useDocumentAgreementEntity()

  const context: DocumentAgreementContext = {
    documentAgreementId: entityState.documentAgreementId,
    documentAgreementSource: entityState.documentAgreementSource,
    getDocumentAgreement: entityState.getDocumentAgreement,
    setDocumentAgreement: entityState.setDocumentAgreement,
    isAuthor: entityState.isAuthor,
    isErm: entityState.isErm,
    readonly: entityState.readonly,
    userId: entityState.userId,
  }

  const filesState = useFiles(context)
  const participantsState = useParticipants(context)
  const relatedDocumentsState = useRelatedDocuments(context.documentAgreementId)
  const relatedSignaturesState = useRelatedSignatures(
    context.documentAgreementId
  )
  const logsState = useLogs(context)
  const routesState = useRoutes(context)
  const extraDocumentsState = useExtraDocuments(context)

  const isInitialized = ref(false)

  const init = () => {
    return handleAsync('initDocumentAgreement', async () => {
      await entityState.getDocumentAgreement()

      await Promise.allSettled([
        entityState.getContractors(),
        filesState.getFiles(),
        participantsState.getParticipants(),
        relatedDocumentsState.getRelatedDocuments(),
        relatedSignaturesState.getRelatedSignatures(),
        logsState.getLogs(),
        routesState.getRoutes(),
      ]).then(() => {
        setCredentials()
        isInitialized.value = true
        subscribeToSocket()
      })
    })
  }

  init()

  return {
    isInitialized,
    ...entityState,
    ...filesState,
    ...participantsState,
    ...relatedDocumentsState,
    ...relatedSignaturesState,
    ...logsState,
    ...routesState,
    ...extraDocumentsState,
  }
})
